/** Read-only colorized code block (CodeMirror 6, lazy-loaded chunk).
 *
 * Reuses the TemplateCodeEditor theme (`makeEditorTheme` — MUI-palette-driven
 * chrome + lezer-tag highlight style); `@codemirror/lang-xml` supplies the
 * grammar for import documents. Line wrapping is a prop so `DocumentViewer`
 * can offer a wrap toggle for long attribute-heavy lines.
 */
import { Box, useTheme } from '@mui/material';
import { useEffect, useRef } from 'react';

import type { EditorPalette } from '@/components/TemplateCodeEditor/themes';

export interface CodeViewerProps {
  value: string;
  language: 'xml' | 'none';
  wrap?: boolean;
  maxHeight?: number | string;
}

export function CodeViewer({ value, language, wrap = false, maxHeight = 560 }: CodeViewerProps) {
  const theme = useTheme();
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let view: { destroy(): void } | null = null;
    let cancelled = false;
    const host = hostRef.current;
    void (async () => {
      const [{ EditorView, lineNumbers }, { EditorState }, { xml }, { makeEditorTheme }] =
        await Promise.all([
          import('@codemirror/view'),
          import('@codemirror/state'),
          import('@codemirror/lang-xml'),
          import('@/components/TemplateCodeEditor/themes'),
        ]);
      if (cancelled || !host) return;
      const palette: EditorPalette = {
        mode: theme.palette.mode,
        primary: { main: theme.palette.primary.main },
        error: { main: theme.palette.error.main },
        warning: { main: theme.palette.warning.main },
        divider: theme.palette.divider,
        text: {
          primary: theme.palette.text.primary,
          secondary: theme.palette.text.secondary,
          disabled: theme.palette.text.disabled,
        },
        background: {
          paper: theme.palette.background.paper,
          default: theme.palette.background.default,
        },
      };
      const extensions = [
        lineNumbers(),
        EditorState.readOnly.of(true),
        EditorView.editable.of(false),
        makeEditorTheme(palette),
        ...(wrap ? [EditorView.lineWrapping] : []),
        ...(language === 'xml' ? [xml()] : []),
      ];
      host.textContent = '';
      view = new EditorView({
        parent: host,
        state: EditorState.create({ doc: value, extensions }),
      });
    })();
    return () => {
      cancelled = true;
      view?.destroy();
    };
  }, [value, language, wrap, theme]);

  return (
    <Box
      ref={hostRef}
      sx={{
        maxHeight,
        overflow: 'auto',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        '& .cm-editor': { fontSize: 12.5 },
      }}
    />
  );
}
